
import { defineComponent, computed, watch, ref, onMounted, PropType } from 'vue'
import draggable from 'vuedraggable'

type Primitive = string | number
type Option = Record<string, any>

export default defineComponent({
    name: 'SelectedOrderChips',
    components: { draggable },
    props: {
        modelValue: {
            type: [Array, String, Number, null] as PropType<Primitive[] | Primitive | null>,
            default: null
        },
        options: {
            type: Array as PropType<Option[]>,
            default: () => []
        },
        labelProp: {
            type: String as PropType<string>,
            default: 'name'
        }
    },
    emits: {
        'update:modelValue': (val: any) => Array.isArray(val) || val === null || typeof val === 'string' || typeof val === 'number'
    },
    setup(props, { emit }) {
        const keyName = 'k' // απλό, χωρίς _ ή $
        const labelKey = computed(() => props.labelProp)

        // Το “εξωτερικό” μοντέλο ως array (ή null)
        const modelArray = computed<Primitive[] | null>(() =>
            Array.isArray(props.modelValue) ? (props.modelValue as Primitive[]) : null
        )
        const singleValue = computed<Primitive | null>(() =>
            Array.isArray(props.modelValue) ? null : (props.modelValue as Primitive | null)
        )
        const hasSingle = computed(() =>
            singleValue.value !== null &&
            singleValue.value !== undefined &&
            singleValue.value !== ''
        )

        // Εσωτερική λίστα για draggable (ref, ΟΧΙ computed)
        const internalList = ref<Array<Record<string, Primitive>>>([])

        // Helper: σύγκριση array περιεχομένου (γρήγορη)
        const sameArray = (a: Primitive[] | null, b: Primitive[] | null) => {
            if (a === b) return true
            if (!a || !b || a.length !== b.length) return false
            for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
            return true
        }

        // Sync: props -> internal (στην αρχή & όταν αλλάζει το μοντέλο)
        const rebuildInternal = () => {
            const ids = modelArray.value ?? []
            internalList.value = ids.map(id => ({ [keyName]: id }))
        }

        onMounted(rebuildInternal)
        watch(modelArray, rebuildInternal, { immediate: true })

        // Sync: internal -> emit προς τα έξω (μόνο όταν **όντως** άλλαξε η σειρά)
        watch(
            internalList,
            (list) => {
                const newIds = list.map(o => o[keyName])
                const oldIds = modelArray.value ?? []
                if (!sameArray(newIds, oldIds)) {
                    emit('update:modelValue', newIds)
                }
            },
            { deep: true }
        )

        const byId = computed<Record<Primitive, Option>>(() =>
            Object.fromEntries((props.options || []).map(o => [o.id as Primitive, o]))
        )
        const singleLabel = computed(() => {
            const id = singleValue.value
            if (id === null || id === undefined) return ''
            return byId.value[id]?.[labelKey.value] ?? id
        })
        const removeId = (id: Primitive) => {
        // βγάζουμε το item από την εσωτερική λίστα
        internalList.value = internalList.value.filter(o => o[keyName] !== id)
        // ο watcher που έχεις ήδη θα κάνει emit το νέο array ids προς τα έξω
        }
        const removeSingle = () => emit('update:modelValue', null)
        return { internalList, modelArray, byId, keyName, labelKey, removeId, singleValue, singleLabel, hasSingle, removeSingle }
    }
})
